"use client"

import React from "react"
import { useState, useEffect, useRef } from "react"
import {
  Activity,
  Bus,
  Car,
  Check,
  Clock,
  Locate,
  MapPin,
  Route,
  Truck,
  X,
  AlertCircle,
  RefreshCcw,
  Loader2,
  AlertOctagon,
  Phone,
  Navigation,
  User,
  Calendar,
  FileText,
  MapIcon,
} from "lucide-react"

// Modificar la función ServiceMap para que actualice las coordenadas GPS del servicio
// Añadir un nuevo prop onCoordsUpdate para pasar las coordenadas actualizadas al componente padre
const ServiceMap = ({ latitude, longitude, serviceName, originAddress, destinationAddress, onCoordsUpdate }) => {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const routingControlRef = useRef(null)
  const [originCoords, setOriginCoords] = useState(null)
  const [destCoords, setDestCoords] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Actualizar las coordenadas GPS cuando se obtienen coordenadas exactas
  useEffect(() => {
    if (originCoords && destCoords && !originCoords.isApproximate && !destCoords.isApproximate && onCoordsUpdate) {
      onCoordsUpdate({
        originLat: originCoords.lat,
        originLng: originCoords.lng,
        destLat: destCoords.lat,
        destLng: destCoords.lng,
      })
    }
  }, [originCoords, destCoords, onCoordsUpdate])

  // Función para geocodificar direcciones
  const geocodeAddress = async (address) => {
    try {
      if (!address) return null

      // Añadir "Bogotá, Colombia" a la dirección si no se especifica una ciudad
      let searchAddress = address
      if (
        !searchAddress.toLowerCase().includes("bogotá") &&
        !searchAddress.toLowerCase().includes("bogota") &&
        !searchAddress.toLowerCase().includes("colombia")
      ) {
        searchAddress = `${searchAddress}, Bogotá, Colombia`
      }

      // Formatear direcciones colombianas para mejor compatibilidad con Nominatim
      // Convertir formatos como "Carrera 69 B 98 a 10" a "Carrera 69B #98a-10"
      searchAddress = searchAddress
        .replace(/\s+([0-9]+)\s+([a-zA-Z])\s+([0-9]+)\s+([a-zA-Z])\s+([0-9]+)/gi, " $1$2 #$3$4-$5")
        .replace(/\s+([0-9]+)\s+([a-zA-Z])\s+([0-9]+)/gi, " $1$2 #$3")
        .replace(/cra\.\s+/gi, "Carrera ")
        .replace(/cra\s+/gi, "Carrera ")
        .replace(/cll\.\s+/gi, "Calle ")
        .replace(/cll\s+/gi, "Calle ")

      console.log("Buscando dirección:", searchAddress)

      // Intentar primero con la API de Nominatim
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}&limit=1&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "es",
            "User-Agent": "TransportServiceMonitor/1.0",
          },
        },
      )

      const data = await response.json()

      if (data && data.length > 0) {
        return {
          lat: Number.parseFloat(data[0].lat),
          lng: Number.parseFloat(data[0].lon),
          display_name: data[0].display_name,
          originalAddress: address,
        }
      }

      // Si Nominatim falla, intentar con la API de Geocodificación de Google (simulada)
      // En un entorno real, aquí se usaría la API de Google con una clave válida
      // Esta es una simulación basada en coordenadas aproximadas de Bogotá

      // Simulación de geocodificación para direcciones específicas
      if (address.includes("Carrera 69 B 98 a 10") || address.includes("Cra. 69B #98a-10")) {
        return {
          lat: 4.6954,
          lng: -74.0754,
          display_name: "Carrera 69B #98a-10, Bogotá, Colombia",
          originalAddress: address,
        }
      }

      if (address.includes("Cra. 18a #44-52") || address.includes("Carrera 18a #44-52")) {
        return {
          lat: 4.6321,
          lng: -74.0708,
          display_name: "Carrera 18a #44-52, Bogotá, Colombia",
          originalAddress: address,
        }
      }

      // Generar coordenadas aleatorias dentro de Bogotá para otras direcciones
      // Esto es solo para demostración, en producción se usaría un servicio real
      const bogotaLat = 4.6097 + (Math.random() * 0.1 - 0.05)
      const bogotaLng = -74.0817 + (Math.random() * 0.1 - 0.05)

      return {
        lat: bogotaLat,
        lng: bogotaLng,
        display_name: `${address} (aproximado), Bogotá, Colombia`,
        originalAddress: address,
        isApproximate: true,
      }
    } catch (error) {
      console.error("Error geocodificando dirección:", error)
      return null
    }
  }

  // Función para encontrar ubicaciones cercanas
  const findNearbyPlaces = async (lat, lng, type = "amenity") => {
    try {
      // Usar Overpass API para encontrar lugares cercanos
      const radius = 1000 // 1km
      const query = `
        [out:json];
        node["${type}"](around:${radius},${lat},${lng});
        out 5;
      `
      const response = await fetch("https://overpass-api.de/api/interpreter", {
        method: "POST",
        body: query,
      })
      const data = await response.json()
      return data.elements || []
    } catch (error) {
      console.error("Error buscando lugares cercanos:", error)
      return []
    }
  }

  useEffect(() => {
    // Geocodificar direcciones al inicio
    const setupCoordinates = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Intentar geocodificar direcciones reales primero
        let originResult = null
        let destResult = null

        if (originAddress) {
          originResult = await geocodeAddress(originAddress)
          console.log("Resultado origen:", originResult)
        }

        if (destinationAddress) {
          destResult = await geocodeAddress(destinationAddress)
          console.log("Resultado destino:", destResult)
        }

        // Si tenemos resultados de geocodificación, usarlos
        if (originResult && destResult) {
          setOriginCoords(originResult)
          setDestCoords(destResult)
        } else {
          // Si falta alguna coordenada, usar la posición actual como referencia
          const defaultLat = latitude ? Number.parseFloat(latitude) : 4.6097
          const defaultLng = longitude ? Number.parseFloat(longitude) : -74.0817

          if (!originResult && originAddress) {
            setOriginCoords({
              lat: defaultLat - 0.01,
              lng: defaultLng - 0.01,
              isApproximate: true,
              display_name: originAddress || "Ubicación aproximada",
            })
          } else if (originResult) {
            setOriginCoords(originResult)
          } else {
            setOriginCoords({
              lat: defaultLat - 0.01,
              lng: defaultLng - 0.01,
              isApproximate: true,
              display_name: "Ubicación aproximada",
            })
          }

          if (!destResult && destinationAddress) {
            setDestCoords({
              lat: defaultLat + 0.01,
              lng: defaultLng + 0.01,
              isApproximate: true,
              display_name: destinationAddress || "Ubicación aproximada",
            })
          } else if (destResult) {
            setDestCoords(destResult)
          } else {
            setDestCoords({
              lat: defaultLat + 0.01,
              lng: defaultLng + 0.01,
              isApproximate: true,
              display_name: "Ubicación aproximada",
            })
          }
        }
      } catch (err) {
        console.error("Error configurando coordenadas:", err)
        setError("No se pudieron determinar las coordenadas")

        // Usar coordenadas por defecto en caso de error (Bogotá)
        setOriginCoords({
          lat: 4.6097,
          lng: -74.0817,
          isApproximate: true,
          display_name: "Error de geocodificación",
        })
        setDestCoords({
          lat: 4.6297,
          lng: -74.0617,
          isApproximate: true,
          display_name: "Error de geocodificación",
        })
      } finally {
        setIsLoading(false)
      }
    }

    setupCoordinates()
  }, [latitude, longitude, originAddress, destinationAddress])

  useEffect(() => {
    // Cargar Leaflet y plugins dinámicamente
    const loadLeaflet = async () => {
      if (typeof window === "undefined") return

      // Verificar si Leaflet ya está cargado
      if (window.L && window.L.Routing) {
        initializeMap()
        return
      }

      // Cargar CSS de Leaflet
      const leafletCSS = document.createElement("link")
      leafletCSS.rel = "stylesheet"
      leafletCSS.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
      leafletCSS.integrity = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY="
      leafletCSS.crossOrigin = ""
      document.head.appendChild(leafletCSS)

      // Cargar CSS de Leaflet Routing Machine
      const routingCSS = document.createElement("link")
      routingCSS.rel = "stylesheet"
      routingCSS.href = "https://unpkg.com/leaflet-routing-machine@3.2.12/dist/leaflet-routing-machine.css"
      document.head.appendChild(routingCSS)

      // Cargar JavaScript de Leaflet
      const leafletScript = document.createElement("script")
      leafletScript.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
      leafletScript.integrity = "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo="
      leafletScript.crossOrigin = ""

      leafletScript.onload = () => {
        // Cargar Leaflet Routing Machine después de Leaflet
        const routingScript = document.createElement("script")
        routingScript.src = "https://unpkg.com/leaflet-routing-machine@3.2.12/dist/leaflet-routing-machine.js"
        routingScript.onload = initializeMap
        document.head.appendChild(routingScript)
      }

      document.head.appendChild(leafletScript)
    }

    const initializeMap = () => {
      if (!mapRef.current || !window.L || !originCoords || !destCoords) return

      try {
        // Limpiar mapa existente
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove()
        }

        // Crear nuevo mapa centrado en el punto medio entre origen y destino
        const centerLat = (originCoords.lat + destCoords.lat) / 2
        const centerLng = (originCoords.lng + destCoords.lng) / 2

        mapInstanceRef.current = window.L.map(mapRef.current).setView([centerLat, centerLng], 13)

        // Agregar tiles
        window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
        }).addTo(mapInstanceRef.current)

        // Iconos personalizados para origen y destino
        const originIcon = window.L.divIcon({
          html: `<div style="background-color: #28a745; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
          className: "custom-marker",
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        })

        const destinationIcon = window.L.divIcon({
          html: `<div style="background-color: #dc3545; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
          className: "custom-marker",
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        })

        // Agregar marcadores de origen y destino con información mejorada
        const originMarker = window.L.marker([originCoords.lat, originCoords.lng], { icon: originIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup(
            `<strong>Origen:</strong><br>${originCoords.display_name || originAddress || "Punto de inicio"}
            ${
              originCoords.isApproximate
                ? '<br><span style="color:#f59e0b;font-size:11px;"><b>⚠️ Ubicación aproximada</b></span>'
                : `<br>Lat: ${originCoords.lat.toFixed(6)}, Lng: ${originCoords.lng.toFixed(6)}`
            }`,
          )

        const destinationMarker = window.L.marker([destCoords.lat, destCoords.lng], { icon: destinationIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup(
            `<strong>Destino:</strong><br>${destCoords.display_name || destinationAddress || "Punto de llegada"}
            ${
              destCoords.isApproximate
                ? '<br><span style="color:#f59e0b;font-size:11px;"><b>⚠️ Ubicación aproximada</b></span>'
                : `<br>Lat: ${destCoords.lat.toFixed(6)}, Lng: ${destCoords.lng.toFixed(6)}`
            }`,
          )

        // Agregar ruta si Leaflet Routing Machine está disponible
        if (window.L.Routing) {
          routingControlRef.current = window.L.Routing.control({
            waypoints: [
              window.L.latLng(originCoords.lat, originCoords.lng),
              window.L.latLng(destCoords.lat, destCoords.lng),
            ],
            routeWhileDragging: false,
            addWaypoints: false,
            createMarker: () => null, // No crear marcadores automáticos
            lineOptions: {
              styles: [
                {
                  color: "#95fc00",
                  weight: 6,
                  opacity: 0.8,
                },
              ],
            },
            show: false, // Ocultar las instrucciones de navegación
            router: window.L.Routing.osrmv1({
              serviceUrl: "https://router.project-osrm.org/route/v1",
            }),
          }).addTo(mapInstanceRef.current)

          // Ocultar el panel de instrucciones
          const routingContainer = mapInstanceRef.current.getContainer().querySelector(".leaflet-routing-container")
          if (routingContainer) {
            routingContainer.style.display = "none"
          }
        } else {
          // Si no hay routing disponible, dibujar una línea simple
          const routeLine = window.L.polyline(
            [
              [originCoords.lat, originCoords.lng],
              [destCoords.lat, destCoords.lng],
            ],
            {
              color: "#95fc00",
              weight: 4,
              opacity: 0.7,
              dashArray: "10, 10",
            },
          ).addTo(mapInstanceRef.current)
        }

        // Ajustar la vista para mostrar todos los puntos
        const group = new window.L.featureGroup([originMarker, destinationMarker])
        mapInstanceRef.current.fitBounds(group.getBounds().pad(0.1))

        // Agregar marcador de posición actual (si está disponible)
        if (latitude && longitude) {
          const currentLat = Number.parseFloat(latitude)
          const currentLng = Number.parseFloat(longitude)

          const currentIcon = window.L.divIcon({
            html: `<div style="background-color: #ffc107; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
            className: "current-position-marker",
            iconSize: [16, 16],
            iconAnchor: [8, 8],
          })

          window.L.marker([currentLat, currentLng], { icon: currentIcon })
            .addTo(mapInstanceRef.current)
            .bindPopup(
              `<strong>Posición Actual:</strong><br>${serviceName || "Vehículo"}<br>Lat: ${currentLat.toFixed(6)}, Lng: ${currentLng.toFixed(6)}`,
            )
        }

        // Buscar y mostrar lugares cercanos al destino
        findNearbyPlaces(destCoords.lat, destCoords.lng).then((places) => {
          if (places && places.length > 0) {
            const nearbyIcon = window.L.divIcon({
              html: `<div style="background-color: #6c757d; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
              className: "nearby-marker",
              iconSize: [12, 12],
              iconAnchor: [6, 6],
            })

            places.forEach((place) => {
              if (place.lat && place.lon) {
                window.L.marker([place.lat, place.lon], { icon: nearbyIcon })
                  .addTo(mapInstanceRef.current)
                  .bindPopup(
                    `<strong>Lugar cercano:</strong><br>${place.tags?.name || "Sin nombre"}<br>Tipo: ${place.tags?.amenity || "No especificado"}`,
                  )
              }
            })
          }
        })
      } catch (error) {
        console.error("Error inicializando mapa:", error)
      }
    }

    if (originCoords && destCoords) {
      loadLeaflet()
    }

    return () => {
      if (routingControlRef.current && mapInstanceRef.current) {
        try {
          mapInstanceRef.current.removeControl(routingControlRef.current)
        } catch (e) {
          console.log("Error removing routing control:", e)
        }
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [originCoords, destCoords, serviceName, latitude, longitude])

  return (
    <div
      ref={mapRef}
      style={{
        height: "400px",
        width: "100%",
        borderRadius: "12px",
        backgroundColor: "#f0f2f5",
        border: "2px solid #e1e8ed",
        position: "relative",
      }}
    >
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1000,
            borderRadius: "12px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Loader2
              style={{ width: "40px", height: "40px", animation: "spin 1s linear infinite", marginBottom: "10px" }}
            />
            <div>Cargando mapa y calculando ruta...</div>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1000,
            borderRadius: "12px",
          }}
        >
          <div style={{ textAlign: "center", color: "#dc3545" }}>
            <AlertCircle style={{ width: "40px", height: "40px", marginBottom: "10px" }} />
            <div>{error}</div>
          </div>
        </div>
      )}

      {/* Leyenda del mapa */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          background: "rgba(255, 255, 255, 0.95)",
          padding: "10px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          zIndex: 1000,
          fontSize: "12px",
          minWidth: "120px",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#333" }}>Leyenda</div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#28a745",
              borderRadius: "50%",
              marginRight: "6px",
            }}
          ></div>
          <span>Origen</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#dc3545",
              borderRadius: "50%",
              marginRight: "6px",
            }}
          ></div>
          <span>Destino</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#ffc107",
              borderRadius: "50%",
              marginRight: "6px",
            }}
          ></div>
          <span>Actual</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#6c757d",
              borderRadius: "50%",
              marginRight: "6px",
            }}
          ></div>
          <span>Cercanos</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ width: "20px", height: "3px", backgroundColor: "#95fc00", marginRight: "6px" }}></div>
          <span>Ruta</span>
        </div>
      </div>

      {/* Información de coordenadas */}
      {originCoords && destCoords && (
        <div
          style={{
            position: "absolute",
            bottom: "10px",
            left: "10px",
            background: "rgba(255, 255, 255, 0.95)",
            padding: "10px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 1000,
            fontSize: "11px",
            maxWidth: "300px",
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "4px", color: "#333" }}>Información de Ubicación</div>
          <div style={{ marginBottom: "4px" }}>
            <span style={{ color: "#28a745", fontWeight: "bold" }}>Origen:</span>{" "}
            {originCoords.isApproximate ? (
              <span>
                <span style={{ color: "#f59e0b" }}>Aproximado</span> - {originCoords.display_name || originAddress}
              </span>
            ) : (
              <span>
                <span style={{ color: "#28a745" }}>Exacto</span> - {originCoords.display_name || originAddress}
                <div style={{ fontSize: "5px", color: "#666", marginTop: "2px" }}>
                  {originCoords.lat.toFixed(6)}, {originCoords.lng.toFixed(6)}
                </div>
              </span>
            )}
          </div>
          <div>
            <span style={{ color: "#dc3545", fontWeight: "bold" }}>Destino:</span>{" "}
            {destCoords.isApproximate ? (
              <span>
                <span style={{ color: "#f59e0b" }}>Aproximado</span> - {destCoords.display_name || destinationAddress}
              </span>
            ) : (
              <span>
                <span style={{ color: "#28a745" }}>Exacto</span> - {destCoords.display_name || destinationAddress}
                <div style={{ fontSize: "5px", color: "#666", marginTop: "2px" }}>
                  {destCoords.lat.toFixed(6)}, {destCoords.lng.toFixed(6)}
                </div>
              </span>
            )}
          </div>
  
        </div>
      )}
    </div>
  )
}

export default function DashboardConectado() {
  const [services, setServices] = useState([])
  const [serviceStatuses, setServiceStatuses] = useState([])
  const [selectedService, setSelectedService] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [activeTab, setActiveTab] = useState("todos")
  const [stylesLoaded, setStylesLoaded] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(new Date())
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // ID único y persistente para los estilos
  const dashboardId = useRef("dashboard-" + Math.random().toString(36).substring(2, 9)).current

  // Load styles when component mounts - MEJORADO
  useEffect(() => {
    const loadStyles = () => {
      // Verificar si los estilos ya están cargados para este ID específico
      const existingBootstrap = document.getElementById(`bootstrap-styles-${dashboardId}`)
      const existingDashboard = document.getElementById(`dashboard-styles-${dashboardId}`)

      if (existingBootstrap && existingDashboard) {
        setStylesLoaded(true)
        return
      }

      // Remover estilos anteriores si existen
      const oldBootstrap = document.querySelectorAll('[id^="bootstrap-styles-dashboard-"]')
      const oldDashboard = document.querySelectorAll('[id^="dashboard-styles-dashboard-"]')
      oldBootstrap.forEach((el) => el.remove())
      oldDashboard.forEach((el) => el.remove())

      const bootstrapStyle = document.createElement("style")
      bootstrapStyle.id = `bootstrap-styles-${dashboardId}`
      bootstrapStyle.innerHTML = `
        /* Scoped Bootstrap styles */
        #${dashboardId} .container, #${dashboardId} .container-fluid, #${dashboardId} .row, #${dashboardId} .col, 
        #${dashboardId} .col-md-6, #${dashboardId} .col-lg-3, #${dashboardId} .col-lg-4, #${dashboardId} .col-lg-8 {
          box-sizing: border-box;
          width: 100%;
        }
        #${dashboardId} .row {
          display: flex;
          flex-wrap: wrap;
          margin-right: -0.75rem;
          margin-left: -0.75rem;
        }
        #${dashboardId} .col, #${dashboardId} .col-md-6, #${dashboardId} .col-lg-3, #${dashboardId} .col-lg-4, #${dashboardId} .col-lg-8 {
          position: relative;
          padding-right: 0.75rem;
          padding-left: 0.75rem;
        }
        @media (min-width: 768px) {
          #${dashboardId} .col-md-6 {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }
        @media (min-width: 992px) {
          #${dashboardId} .col-lg-3 {
            flex: 0 0 25%;
            max-width: 25%;
          }
          #${dashboardId} .col-lg-4 {
            flex: 0 0 33.333333%;
            max-width: 33.333333%;
          }
          #${dashboardId} .col-lg-8 {
            flex: 0 0 66.666667%;
            max-width: 66.666667%;
          }
        }
        #${dashboardId} .g-4 {
          --bs-gutter-x: 1.5rem;
          --bs-gutter-y: 1.5rem;
          margin-right: calc(var(--bs-gutter-x) * -0.5);
          margin-left: calc(var(--bs-gutter-x) * -0.5);
        }
        #${dashboardId} .g-4 > * {
          padding-right: calc(var(--bs-gutter-x) * 0.5);
          padding-left: calc(var(--bs-gutter-x) * 0.5);
          margin-top: var(--bs-gutter-y);
        }
        #${dashboardId} .mb-4 {
          margin-bottom: 1.5rem;
        }
        #${dashboardId} .mb-2 {
          margin-bottom: 0.5rem;
        }
        #${dashboardId} .mt-4 {
          margin-top: 1.5rem;
        }
        #${dashboardId} .ms-1 {
          margin-left: 0.25rem;
        }
        #${dashboardId} .ms-auto {
          margin-left: auto;
        }
        #${dashboardId} .ml-auto {
          margin-left: auto;
        }
        #${dashboardId} .d-flex {
          display: flex;
        }
        #${dashboardId} .justify-content-between {
          justify-content: space-between;
        }
        #${dashboardId} .align-items-center {
          align-items: center;
        }
        #${dashboardId} .flex-1 {
          flex: 1;
        }
        #${dashboardId} .h-100 {
          height: 100%;
        }
        #${dashboardId} .fw-bold {
          font-weight: 700;
        }
        #${dashboardId} .display-5 {
          font-size: 2.5rem;
          font-weight: 300;
          line-height: 1.2;
        }
        #${dashboardId} .card {
          position: relative;
          display: flex;
          flex-direction: column;
          min-width: 0;
          word-wrap: break-word;
          background-color: #fff;
          background-clip: border-box;
          border: 1px solid rgba(0, 0, 0, 0.125);
          border-radius: 0.25rem;
        }
        #${dashboardId} .card-body {
          flex: 1 1 auto;
          padding: 1.25rem;
        }
        #${dashboardId} .card-header {
          padding: 0.75rem 1.25rem;
          margin-bottom: 0;
          background-color: rgba(0, 0, 0, 0.03);
          border-bottom: 1px solid rgba(0, 0, 0, 0.125);
        }
        #${dashboardId} .card-title {
          margin-bottom: 0.75rem;
          font-size: 1.25rem;
        }
        #${dashboardId} .table {
          width: 100%;
          margin-bottom: 1rem;
          color: #212529;
          border-collapse: collapse;
        }
        #${dashboardId} .table th,
        #${dashboardId} .table td {
          padding: 0.75rem;
          vertical-align: top;
          border-top: 1px solid #dee2e6;
        }
        #${dashboardId} .table thead th {
          vertical-align: bottom;
          border-bottom: 2px solid #dee2e6;
        }
        #${dashboardId} .table-responsive {
          display: block;
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        #${dashboardId} .text-muted {
          color: #6c757d;
        }
        #${dashboardId} .text-capitalize {
          text-transform: capitalize;
        }
      `

      const dashboardStyle = document.createElement("style")
      dashboardStyle.id = `dashboard-styles-${dashboardId}`
      dashboardStyle.innerHTML = `
        /* Pagination styles */
        #${dashboardId} .pagination {
          display: flex;
          gap: 0.25rem;
        }
        
        #${dashboardId} .page-item {
          display: flex;
          align-items: center;
        }
        
        #${dashboardId} .page-link {
          padding: 0.35rem 0.7rem;
          border: 1px solid #dee2e6;
          background: white;
          color: var(--primary);
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-decoration: none;
          font-size: 0.875rem;
        }
        
        #${dashboardId} .page-item.active .page-link {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
        }
        
        #${dashboardId} .page-item.disabled .page-link {
          color: #6c757d;
          pointer-events: none;
          background-color: #fff;
          border-color: #dee2e6;
        }
        
        #${dashboardId} .page-link:hover:not(:disabled) {
          background-color: #e9ecef;
          border-color: #dee2e6;
        }
        
        #${dashboardId} .page-item.active .page-link:hover {
          background: var(--primary-dark);
          border-color: var(--primary-dark);
        }
        
        /* Dashboard styles - MEJORADO */
        #${dashboardId} {
          --primary: #667eea;
          --primary-light: #f0f3ff;
          --primary-dark: #5a67d8;
          --secondary: #6c757d;
          --success: #48bb78;
          --success-light: #f0fff4;
          --success-dark: #38a169;
          --warning: #ed8936;
          --warning-light: #fffaf0;
          --warning-dark: #dd6b20;
          --danger: #f56565;
          --danger-light: #fed7d7;
          --danger-dark: #e53e3e;
          --info: #4299e1;
          --info-light: #ebf8ff;
          --info-dark: #3182ce;
          --purple: #9f7aea;
          --purple-light: #faf5ff;
          --purple-dark: #805ad5;
          --light: #f7fafc;
          --dark: #2d3748;
          --gray: #a0aec0;
          --gray-light: #edf2f7;
          --gray-dark: #4a5568;
          --border-radius: 12px;
          --border-radius-sm: 8px;
          --border-radius-lg: 16px;
          --shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
          --shadow-lg: 0 10px 25px rgba(0, 0, 0, 0.15);
          --shadow-xl: 0 20px 40px rgba(0, 0, 0, 0.1);
          --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          --gradient-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --gradient-success: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
          --gradient-warning: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
          --gradient-danger: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
          --gradient-complete: linear-gradient(135deg,rgb(84, 178, 255) 0%,rgb(62, 176, 229) 100%);
        }

        #${dashboardId} .dashboard-container {
          padding: 1.5rem;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
        }

        #${dashboardId} .header-gradient {
          background: var(--gradient-primary);
          color: white;
          border-radius: var(--border-radius-lg);
          padding: 2.5rem;
          position: relative;
          overflow: hidden;
          box-shadow: var(--shadow-lg);
          backdrop-filter: blur(10px);
        }

        #${dashboardId} .header-content {
          position: relative;
          z-index: 2;
        }

        #${dashboardId} .header-subtitle {
          opacity: 0.9;
          font-size: 1.1rem;
          font-weight: 300;
        }

        #${dashboardId} .header-circle-1,
        #${dashboardId} .header-circle-2 {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(5px);
        }

        #${dashboardId} .header-circle-1 {
          width: 300px;
          height: 300px;
          top: -150px;
          right: -100px;
        }

        #${dashboardId} .header-circle-2 {
          width: 200px;
          height: 200px;
          bottom: -100px;
          left: 10%;
        }

        #${dashboardId} .tab-container {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        #${dashboardId} .tab-button {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          padding: 0.75rem 1.25rem;
          border-radius: 25px;
          cursor: pointer;
          transition: var(--transition);
          font-weight: 500;
          backdrop-filter: blur(10px);
        }

        #${dashboardId} .tab-button:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        #${dashboardId} .tab-active {
          background: white;
          color: var(--primary);
          font-weight: 600;
          box-shadow: var(--shadow);
        }

        #${dashboardId} .stat-card {
          border-radius: var(--border-radius-lg);
          padding: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: var(--shadow-lg);
          transition: var(--transition);
          border: none;
          position: relative;
          overflow: hidden;
          transform: translateY(0);
          transition: all 0.3s ease;
        }
        
        #${dashboardId} .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
        }
        
        #${dashboardId} .stat-card.active-stat {
          border: 3px solid white;
          box-shadow: 0 0 0 2px var(--primary), 0 10px 20px rgba(0, 0, 0, 0.2);
          transform: translateY(-5px);
        }

        #${dashboardId} .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.3) 100%);
        }

        #${dashboardId} .stat-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: var(--shadow-xl);
        }

        #${dashboardId} .stat-primary {
          background: var(--gradient-primary);
          color: white;
        }

        #${dashboardId} .stat-success {
          background: var(--gradient-success);
          color: white;
        }

        #${dashboardId} .stat-warning {
          background: var(--gradient-warning);
          color: white;
        }

        #${dashboardId} .stat-danger {
          background: var(--gradient-danger);
          color: white;
        }

        #${dashboardId} .stat-complete {
          background: var(--gradient-complete);
          color: white;
        }

        #${dashboardId} .stat-content {
          flex: 1;
        }

        #${dashboardId} .stat-label {
          font-size: 0.875rem;
          margin-bottom: 0.5rem;
          opacity: 0.9;
          font-weight: 500;
        }

        #${dashboardId} .stat-value {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 0.25rem;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        #${dashboardId} .stat-subtitle {
          font-size: 0.75rem;
          opacity: 0.8;
          margin: 0;
          font-weight: 400;
        }

        #${dashboardId} .stat-icon-container {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        #${dashboardId} .stat-icon {
          width: 28px;
          height: 28px;
          color: white;
        }

        #${dashboardId} .custom-card {
          border-radius: var(--border-radius-lg);
          border: none;
          box-shadow: var(--shadow);
          transition: var(--transition);
          background: white;
        }

        #${dashboardId} .custom-card:hover {
          box-shadow: var(--shadow-lg);
          transform: translateY(-2px);
        }

        #${dashboardId} .card-title {
          font-weight: 600;
          font-size: 1.25rem;
          color: var(--dark);
          margin: 0;
        }

        #${dashboardId} .btn-link {
          color: var(--primary);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
        }

        #${dashboardId} .btn-link:hover {
          text-decoration: underline;
        }

        #${dashboardId} .icon-sm {
          width: 16px;
          height: 16px;
        }

        #${dashboardId} .icon-md {
          width: 20px;
          height: 20px;
        }

        #${dashboardId} .icon-lg {
          width: 24px;
          height: 24px;
        }

        #${dashboardId} .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          color: var(--secondary);
        }

        #${dashboardId} .loading-spinner {
          margin-bottom: 1rem;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        #${dashboardId} .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          color: var(--danger);
          text-align: center;
        }

        #${dashboardId} .error-icon {
          margin-bottom: 1rem;
          width: 48px;
          height: 48px;
        }

        #${dashboardId} .refresh-button {
          background: var(--gradient-primary);
          color: white;
          border: none;
          border-radius: var(--border-radius);
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          cursor: pointer;
          transition: var(--transition);
          margin-top: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: var(--shadow);
        }

        #${dashboardId} .refresh-button:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        #${dashboardId} .refresh-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        #${dashboardId} .table-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }

        #${dashboardId} .table-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        #${dashboardId} .table-count {
          font-size: 0.875rem;
          color: var(--secondary);
        }

        #${dashboardId} .table-badge {
          background: var(--primary-light);
          color: var(--primary);
          font-size: 0.75rem;
          font-weight: 500;
          padding: 0.375rem 0.875rem;
          border-radius: 20px;
          border: 1px solid var(--primary);
        }

        #${dashboardId} .custom-table {
          margin-bottom: 0;
        }

        #${dashboardId} .custom-table thead th {
          background: var(--light);
          font-weight: 600;
          font-size: 0.875rem;
          color: var(--gray-dark);
          padding: 1rem;
          border-bottom: 2px solid var(--gray-light);
        }

        #${dashboardId} .custom-table tbody tr {
          cursor: pointer;
          transition: var(--transition);
        }

        #${dashboardId} .custom-table tbody tr:hover {
          background: var(--primary-light);
          transform: scale(1.01);
        }

        #${dashboardId} .custom-table td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid var(--gray-light);
        }

        #${dashboardId} .service-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        #${dashboardId} .service-icon {
          width: 45px;
          height: 45px;
          border-radius: var(--border-radius);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: var(--shadow);
        }

        #${dashboardId} .service-id {
          font-weight: 600;
          font-size: 0.875rem;
        }

        #${dashboardId} .service-vehicle {
          font-size: 0.75rem;
          color: var(--secondary);
        }

        #${dashboardId} .route-destination {
          font-weight: 500;
          font-size: 0.875rem;
        }

        #${dashboardId} .route-origin {
          font-size: 0.75rem;
          color: var(--secondary);
        }

        #${dashboardId} .service-status {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        #${dashboardId} .status-indicator {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.8);
        }

        #${dashboardId} .status-text {
          font-size: 0.875rem;
          font-weight: 500;
        }

        #${dashboardId} .bg-primary-light {
          background-color: var(--primary-light);
        }

        #${dashboardId} .bg-success-light {
          background-color: var(--success-light);
        }

        #${dashboardId} .bg-warning-light {
          background-color: var(--warning-light);
        }

        #${dashboardId} .bg-danger-light {
          background-color: var(--danger-light);
        }

        #${dashboardId} .bg-info-light {
          background-color: var(--info-light);
        }

        #${dashboardId} .text-primary {
          color: var(--primary);
        }

        #${dashboardId} .text-success {
          color: var(--success);
        }

        #${dashboardId} .text-warning {
          color: var(--warning);
        }

        #${dashboardId} .text-danger {
          color: var(--danger);
        }

        #${dashboardId} .text-info {
          color: var(--info);
        }

        #${dashboardId} .text-secondary {
          color: var(--secondary);
        }

        #${dashboardId} .update-time {
          font-size: 0.75rem;
          color: var(--secondary);
        }

        #${dashboardId} .btn-details {
          background: var(--gradient-primary);
          color: white;
          border: none;
          border-radius: var(--border-radius);
          padding: 0.5rem 1rem;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
          transition: var(--transition);
          box-shadow: var(--shadow);
        }

        #${dashboardId} .btn-details:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        /* MODAL MEJORADO */
        #${dashboardId} .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
          animation: modalOverlayFadeIn 0.3s ease;
        }

        @keyframes modalOverlayFadeIn {
          from {
            opacity: 0;
            backdrop-filter: blur(0px);
          }
          to {
            opacity: 1;
            backdrop-filter: blur(8px);
          }
        }

        #${dashboardId} .custom-modal {
          background: white;
          border-radius: var(--border-radius-lg);
          width: 100%;
          max-width: 1200px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: var(--shadow-xl);
          animation: modalFadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        @keyframes modalFadeIn {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        #${dashboardId} .modal-header {
          background: var(--gradient-primary);
          color: white;
          padding: 2rem;
          border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        #${dashboardId} .modal-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
          pointer-events: none;
        }

        #${dashboardId} .modal-title-container {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          position: relative;
          z-index: 2;
        }

        #${dashboardId} .modal-icon {
          width: 60px;
          height: 60px;
          border-radius: var(--border-radius);
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        #${dashboardId} .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        #${dashboardId} .modal-status {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          backdrop-filter: blur(10px);
        }

        #${dashboardId} .modal-status-text {
          font-size: 0.875rem;
          font-weight: 500;
        }

        #${dashboardId} .modal-close {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          cursor: pointer;
          padding: 0.75rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: var(--transition);
          backdrop-filter: blur(10px);
          position: relative;
          z-index: 2;
        }

        #${dashboardId} .modal-close:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        #${dashboardId} .modal-body {
          padding: 2rem;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        #${dashboardId} .info-card {
          border-radius: var(--border-radius-lg);
          padding: 2rem;
          box-shadow: var(--shadow-lg);
          border: 1px solid rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          transition: var(--transition);
        }

        #${dashboardId} .info-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-xl);
        }

        #${dashboardId} .info-card-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: var(--dark);
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        #${dashboardId} .info-card-title::before {
          content: '';
          width: 4px;
          height: 24px;
          background: var(--gradient-primary);
          border-radius: 2px;
        }

        #${dashboardId} .info-list {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        #${dashboardId} .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.875rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.7);
          border-radius: var(--border-radius);
          border: 1px solid rgba(0, 0, 0, 0.05);
        }

        #${dashboardId} .info-label {
          color: var(--gray-dark);
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        #${dashboardId} .info-value {
          font-weight: 600;
          color: var(--dark);
        }

        #${dashboardId} .route-details {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid var(--gray-light);
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        #${dashboardId} .route-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.8);
          border-radius: var(--border-radius);
          border-left: 4px solid var(--primary);
        }

        #${dashboardId} .route-label {
          font-size: 0.75rem;
          color: var(--gray-dark);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        #${dashboardId} .route-value-container {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        #${dashboardId} .route-icon {
          width: 18px;
          height: 18px;
          color: var(--primary);
        }

        #${dashboardId} .route-value {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--dark);
        }

        #${dashboardId} .map-container {
          margin-top: 1.5rem;
          border-radius: var(--border-radius-lg);
          overflow: hidden;
          box-shadow: var(--shadow-lg);
          border: 3px solid white;
        }

        #${dashboardId} .map-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
          flex-wrap: wrap;
        }

        #${dashboardId} .btn-map,
        #${dashboardId} .btn-contact {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.5rem;
          border-radius: var(--border-radius);
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition);
          border: none;
          text-decoration: none;
          box-shadow: var(--shadow);
          flex: 1;
          justify-content: center;
          min-width: 160px;
        }

        #${dashboardId} .btn-map {
          background: var(--gradient-primary);
          color: white;
        }

        #${dashboardId} .btn-map:hover {
          transform: translateY(-3px);
          box-shadow: var(--shadow-lg);
        }

        #${dashboardId} .btn-contact {
          background: var(--gradient-success);
          color: white;
        }

        #${dashboardId} .btn-contact:hover {
          transform: translateY(-3px);
          box-shadow: var(--shadow-lg);
        }

        #${dashboardId} .modal-footer {
          padding: 1.5rem 2rem;
          background: var(--light);
          border-top: 1px solid var(--gray-light);
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
        }

        #${dashboardId} .modal-footer-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        #${dashboardId} .footer-system {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--dark);
        }

        #${dashboardId} .footer-update {
          font-size: 0.75rem;
          color: var(--secondary);
        }

        #${dashboardId} .btn-close-modal {
          background: var(--gradient-primary);
          color: white;
          border: none;
          border-radius: var(--border-radius);
          padding: 0.75rem 1.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition);
          box-shadow: var(--shadow);
        }

        #${dashboardId} .btn-close-modal:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        #${dashboardId} .legend-container {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          margin-top: 2rem;
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.8);
          border-radius: var(--border-radius-lg);
          border: 1px solid var(--gray-light);
        }

        #${dashboardId} .legend-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 1rem;
          background: white;
          border-radius: var(--border-radius);
          box-shadow: var(--shadow);
        }

        #${dashboardId} .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.8);
        }

        #${dashboardId} .legend-text {
          font-size: 0.75rem;
          color: var(--gray-dark);
          font-weight: 500;
        }

        #${dashboardId} .dashboard-footer {
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 2px solid var(--gray-light);
        }

        #${dashboardId} .footer-copyright {
          font-size: 0.75rem;
          color: var(--secondary);
          text-align: center;
          margin: 0;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.8);
          border-radius: var(--border-radius);
        }

        @media (max-width: 768px) {
          #${dashboardId} .header-gradient {
            padding: 2rem;
          }

          #${dashboardId} .stat-card {
            padding: 1.5rem;
          }

          #${dashboardId} .stat-value {
            font-size: 1.75rem;
          }

          #${dashboardId} .custom-table thead th {
            padding: 0.75rem;
          }

          #${dashboardId} .custom-table td {
            padding: 0.75rem;
          }

          #${dashboardId} .service-info {
            gap: 0.5rem;
          }

          #${dashboardId} .service-icon {
            width: 35px;
            height: 35px;
          }

          #${dashboardId} .map-actions {
            flex-direction: column;
          }

          #${dashboardId} .btn-map,
          #${dashboardId} .btn-contact {
            justify-content: center;
            min-width: auto;
          }

          #${dashboardId} .modal-header {
            padding: 1.5rem;
          }

          #${dashboardId} .modal-body {
            padding: 1.5rem;
          }

          #${dashboardId} .modal-title-container {
            gap: 1rem;
          }

          #${dashboardId} .modal-icon {
            width: 50px;
            height: 50px;
          }

          #${dashboardId} .modal-title {
            font-size: 1.25rem;
          }
        }
      `

      document.head.appendChild(bootstrapStyle)
      document.head.appendChild(dashboardStyle)
      setStylesLoaded(true)
    }

    loadStyles()

    // NO limpiar estilos en el cleanup para evitar que se pierdan
    return () => {
      // Los estilos se mantienen para evitar pérdida al actualizar
    }
  }, [dashboardId])

  // Función mejorada para fetch con manejo de errores
  const safeFetch = async (url, options = {}) => {
    try {
      const defaultOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        ...options,
      }

      const response = await fetch(url, defaultOptions)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return { success: true, data }
    } catch (error) {
      console.error(`Error fetching ${url}:`, error)
      return { success: false, error: error.message }
    }
  }
  const auth0_profile = JSON.parse(localStorage.getItem("auth0_profile"))
  // Fetch service statuses from API
  const fetchServiceStatuses = async () => {
    const ide = localStorage.getItem("idusuario")
    if (typeof ide !== "undefined") {
    } else {
      const idea = sessionStorage.getItem("idusuario")
      const ide = idea
    }
    const url = `https://app.transorientesas.com/api/exterior/servicios-clientes?clienteId=${ide}`
    const result = await safeFetch(url)

    if (result.success) {
      setServiceStatuses(Array.isArray(result.data.datos) ? result.data.datos : [])
    } else {
      console.error("Error fetching service statuses:", result.error)
      setServiceStatuses([])
    }
  }

  // Fetch services from API
  const fetchServices = async () => {
    try {
      setLoading(true)
      setError(null)

      // First, fetch service statuses
      await fetchServiceStatuses()

      // Try to fetch real services
      let ide = localStorage.getItem("idusuario")
      if (typeof ide !== "undefined") {
      } else {
        const idea = sessionStorage.getItem("idusuario")
        ide = idea
      }
      const url = `https://app.transorientesas.com/api/exterior/servicios-clientes?clienteId=${ide}`
      const result = await safeFetch(url)

      if (result.success) {
        setServices(Array.isArray(result.data.datos) ? result.data.datos : [])
      } else {
        // If the services endpoint doesn't exist, create mock data
        const mockServices = generateMockServices()
        setServices(mockServices)
        setError("Usando datos de demostración - API no disponible")
      }

      setLastUpdated(new Date())
    } catch (err) {
      console.error("Error fetching services:", err)
      setError("Error al cargar los servicios. Mostrando datos de demostración.")
      // Generate mock data as fallback
      const mockServices = generateMockServices()
      setServices(mockServices)
    } finally {
      setLoading(false)
    }
  }

  // Generate mock services based on service statuses
  const generateMockServices = () => {
    const vehicleTypes = ["Taxi", "Bus", "Camión", "Van", "Microbus"]
    const conductors = ["Juan Pérez", "María González", "Carlos Rodríguez", "Ana Martínez", "Roberto Sánchez"]
    const origins = ["Terminal Norte", "Centro Distribución", "Aeropuerto", "Estación Central", "Parqueadero Principal"]
    const destinations = ["Centro Ciudad", "Terminal Sur", "Zona Industrial", "Centro Comercial", "Hotel Plaza"]

    return Array.from({ length: 15 }, (_, index) => {
      const statusId =
        serviceStatuses.length > 0
          ? serviceStatuses[Math.floor(Math.random() * serviceStatuses.length)].id
          : Math.floor(Math.random() * 5) + 1

      const service = {
        id: `SV-${1000 + index}`,
        fecha: new Date().toISOString().split("T")[0],
        aseguradora: 1,
        origen: 1,
        nombre_cliente: `Usuario ${index + 1}`,
        direccion_inicial: origins[Math.floor(Math.random() * origins.length)],
        ciudad: "11001",
        direccion_final: destinations[Math.floor(Math.random() * destinations.length)],
        hora_inicial: "08:00:00",
        vehiculo: vehicleTypes[Math.floor(Math.random() * vehicleTypes.length)],
        conductor: Math.floor(Math.random() * 100) + 1,
        estado_servicio: statusId,
        identificacion: 12345678 + index,
        tarifa: 1,
        tipo_recorrido: Math.floor(Math.random() * 3) + 1,
        numero_expediente: `EXP${1000 + index}`,
        placa: `ABC${100 + index}`,
        numero_contacto: `300${1000000 + index}`,
        latitud: (4.6 + Math.random() * 0.1).toString(),
        longitud: (-74.1 + Math.random() * 0.1).toString(),
        // Additional fields for display
        conductor_nombre: conductors[Math.floor(Math.random() * conductors.length)],
        progreso: Math.floor(Math.random() * 100),
        tiempo_estimado: `${Math.floor(Math.random() * 180)} min`,
        ultima_actualizacion: `hace ${Math.floor(Math.random() * 60)} minutos`,
      }

      return service
    })
  }

  // Get status info by ID
  const getStatusInfo = (statusId) => {
    switch (statusId) {
      case 2:
        return {
          text: "En Ruta",
          name: "En Ruta",
          description: "Servicio se encuentra en ruta",
          colorClass: "text-primary",
          bgClass: "bg-primary-light",
          indicatorClass: "status-primary",
          icon: Route,
          color: "#6c757d",
        }
      case 3:
      case 10:
      case 11:
        return {
          text: "Completado",
          name: "Completado",
          description: "Servicio completado",
          colorClass: "text-success",
          bgClass: "bg-success-light",
          indicatorClass: "status-success",
          icon: Check,
          color: "#28a745",
        }
      case 7:
      case 8:
        return {
          text: "En Progreso",
          name: "En Progreso",
          description: "Servicio en progreso",
          colorClass: "text-warning",
          bgClass: "bg-warning-light",
          indicatorClass: "status-warning",
          icon: Clock,
          color: "#ffc107",
        }
      case 4:
        return {
          text: "Cancelado",
          name: "Cancelado",
          description: "Servicio cancelado",
          colorClass: "text-warning",
          bgClass: "bg-warning-light",
          indicatorClass: "status-warning",
          icon: Clock,
          color: "#dc3545",
        }
      case 6:
        return {
          text: "Pendiente",
          name: "Pendiente",
          description: "Servicio pendiente",
          colorClass: "text-warning",
          bgClass: "bg-warning-light",
          indicatorClass: "status-warning",
          icon: Clock,
          color: "#fd7e14",
        }
      case 1:
        return {
          text: "Asignado a conductor",
          name: "Asignado a conductor",
          description: "Servicio pendiente",
          colorClass: "text-warning",
          bgClass: "bg-warning-light",
          indicatorClass: "status-warning",
          icon: Clock,
          color: "#fd7e14",
        }
      default:
        return {
          text: "Desconocido",
          name: "Desconocido",
          description: "Estado desconocido",
          colorClass: "text-info",
          bgClass: "bg-info-light",
          indicatorClass: "status-info",
          icon: AlertCircle,
          color: "#17a2b8",
        }
    }
  }

  // Get status display properties - LÓGICA ESPECÍFICA DEL USUARIO
  const getStatusDisplay = (statusId) => {
    const statusInfo = getStatusInfo(statusId)

    if (statusId == 2) {
      return {
        text: "En Ruta",
        colorClass: "text-primary",
        bgClass: "bg-primary-light",
        indicatorClass: "status-primary",
        icon: Route,
      }
    } else if (statusId == 3 || statusId == 11 || statusId == 10) {
      return {
        text: "Completado",
        colorClass: "text-success",
        bgClass: "bg-success-light",
        indicatorClass: "status-success",
        icon: Check,
      }
    } else if (statusId == 7 || statusId == 8 || statusId == 9) {
      return {
        text: "En Progreso",
        colorClass: "text-warning",
        bgClass: "bg-warning-light",
        indicatorClass: "status-warning",
        icon: Clock,
      }
    } else if (statusId == 4) {
      return {
        text: "Cancelado",
        colorClass: "text-warning",
        bgClass: "bg-warning-light",
        indicatorClass: "status-warning",
        icon: Clock,
      }
    } else if (statusId == 6) {
      return {
        text: "Pendiente",
        colorClass: "text-warning",
        bgClass: "bg-warning-light",
        indicatorClass: "status-warning",
        icon: Clock,
      }
    } else {
      return {
        text: statusInfo.name,
        colorClass: "text-info",
        bgClass: "bg-info-light",
        indicatorClass: "status-info",
        icon: AlertCircle,
      }
    }
  }

  // Calculate statistics
  const calculateStats = () => {
    if (!services.length) {
      return {
        total: 0,
        en_ruta: 0,
        completados: 0,
        en_progreso: 0,
        cancelados: 0,
        otros: 0,
      }
    }

    const stats = {
      total: services.length,
      en_ruta: 0,
      completados: 0,
      en_progreso: 0,
      cancelados: 0,
      otros: 0,
    }

    services.forEach((service) => {
      const display = getStatusDisplay(service.estado_servicio)
      if (display.text === "En Ruta") {
        stats.en_ruta++
      } else if (display.text === "Completado") {
        stats.completados++
      } else if (display.text === "En Progreso") {
        stats.en_progreso++
      } else if (display.text === "CANCELADO") {
        stats.cancelados++
      } else {
        stats.otros++
      }
    })

    return stats
  }

  // Sort services based on column
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sortable value from service object
  const getSortableValue = (service, key) => {
    switch (key) {
      case 'expediente':
        return service.numero_expediente || '';
      case 'cliente':
        return service.nombre_cliente || '';
      case 'ruta':
        return service.direccion_final || '';
      case 'estado':
        return getStatusDisplay(service.estado_servicio).text;
      case 'conductor':
        return service.conductor_nombre || '';
      default:
        return '';
    }
  };

  // Sort services
  const sortServices = (services) => {
    const sortableItems = [...services];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = getSortableValue(a, sortConfig.key);
        const bValue = getSortableValue(b, sortConfig.key);
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  };

  // Filter services based on active tab and search term
  const getFilteredServices = () => {
    let filtered = services;

    // Apply tab filter
    if (activeTab !== "todos") {
      filtered = filtered.filter((service) => {
        const display = getStatusDisplay(service.estado_servicio);
        switch (activeTab) {
          case "en_ruta":
            return display.text === "En Ruta";
          case "completados":
            return display.text === "Completado";
          case "problemas":
            return display.text === "En Progreso" || display.text.includes("Retraso");
          case "cancelados":
            return display.text === "CANCELADO";
          default:
            return true;
        }
      });
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (service) =>
          (service.numero_expediente && service.numero_expediente.toLowerCase().includes(term)) ||
          (service.nombre_cliente && service.nombre_cliente.toLowerCase().includes(term)) ||
          (service.direccion_final && service.direccion_final.toLowerCase().includes(term)) ||
          (service.conductor_nombre && service.conductor_nombre.toLowerCase().includes(term))
      );
    }

    // Apply sorting
    return sortServices(filtered);
  };

  // Get vehicle icon based on type
  const getVehicleIcon = (vehicleType) => {
    const type = vehicleType?.toLowerCase() || ""

    if (type.includes("bus") || type.includes("microbus")) {
      return Bus
    } else if (type.includes("camión") || type.includes("truck")) {
      return Truck
    } else {
      return Car
    }
  }

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return "N/A"

    try {
      const date = new Date(timeString)
      return date.toLocaleTimeString()
    } catch {
      return timeString
    }
  }

  // Open Google Maps with coordinates
  const openInMaps = (lat, lng, name) => {
    const url = `https://www.google.com/maps?q=${lat},${lng}&z=15&t=m&hl=es&gl=CO&mapclient=embed&cid=${name}`
    window.open(url, "_blank")
  }

  // Call phone number
  const callPhone = (phoneNumber) => {
    window.open(`tel:${phoneNumber}`, "_self")
  }

  // Load data on component mount
  useEffect(() => {
    fetchServices()
  }, [])

  const stats = calculateStats()
  const filteredServices = getFilteredServices()
  
  // Pagination logic
  const totalPages = Math.ceil(filteredServices.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredServices.slice(indexOfFirstItem, indexOfLastItem)
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber)
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, activeTab, sortConfig])
  
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = []
    const maxPagesToShow = 5 // Maximum number of page buttons to show at once
    
    if (totalPages <= maxPagesToShow) {
      // If total pages is less than max pages to show, show all
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      // Always show first page
      pageNumbers.push(1)
      
      // Calculate start and end pages
      let startPage = Math.max(2, currentPage - 1)
      let endPage = Math.min(totalPages - 1, currentPage + 1)
      
      // Adjust if we're at the start or end
      if (currentPage <= 3) {
        endPage = 4
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3
      }
      
      // Add ellipsis if needed
      if (startPage > 2) {
        pageNumbers.push('...')
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        if (i > 1 && i < totalPages) {
          pageNumbers.push(i)
        }
      }
      
      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('...')
      }
      
      // Always show last page
      if (totalPages > 1) {
        pageNumbers.push(totalPages)
      }
    }
    
    return pageNumbers
  }

  if (!stylesLoaded) {
    return <div>Cargando dashboard...</div>
  }

  if (loading && services.length === 0) {
    return (
      <div id={dashboardId}>
        <div className="dashboard-container">
          <div className="loading-container">
            <Loader2 className="loading-spinner icon-lg" />
            <h3>Cargando servicios...</h3>
            <p>Conectando con la API de servicios</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div id={dashboardId}>
      <div className="dashboard-container">
        {/* Header */}
        <header className="header-gradient mb-4">
          <div className="header-content">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="display-5 fw-bold mb-2">Sistema de Monitoreo de Servicios</h1>
                <p className="header-subtitle">Transoriente - {localStorage.getItem('Name')} Movilizandonos hacia el futuro.</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <button onClick={fetchServices} className="refresh-button" disabled={loading}>
                  {loading ? <Loader2 className="icon-sm loading-spinner" /> : <RefreshCcw className="icon-sm" />}
                  {loading ? "Actualizando..." : "Actualizar"}
                </button>
              </div>
            </div>

            {/* <div className="tab-container mt-4">
              <button
                onClick={() => setActiveTab("todos")}
                className={`tab-button ${activeTab === "todos" ? "tab-active" : ""}`}
              >
                Todos ({stats.total})
              </button>
              <button
                onClick={() => setActiveTab("en_ruta")}
                className={`tab-button ${activeTab === "en_ruta" ? "tab-active" : ""}`}
              >
                En Ruta ({stats.en_ruta})
              </button>
              <button
                onClick={() => setActiveTab("completados")}
                className={`tab-button ${activeTab === "completados" ? "tab-active" : ""}`}
              >
                Completados ({stats.completados})
              </button>
              <button
                onClick={() => setActiveTab("problemas")}
                className={`tab-button ${activeTab === "problemas" ? "tab-active" : ""}`}
              >
                En Progreso ({stats.en_progreso})
              </button>
              <button
                onClick={() => setActiveTab("cancelados")}
                className={`tab-button ${activeTab === "cancelados" ? "tab-active" : ""}`}
              >
                Cancelados ({stats.cancelados})
              </button>
            </div> */}
          </div>

          <div className="header-circle-1"></div>
          <div className="header-circle-2"></div>
        </header>

        {error && (
          <div className="error-container mb-4">
            <AlertOctagon className="error-icon" />
            <h3>Información del Sistema</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="row mb-4" style={{ gap: 0 }}>
          <div className="col">
            <div 
              className={`stat-card stat-primary ${activeTab === 'todos' ? 'active-stat' : ''}`}
              onClick={() => setActiveTab('todos')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-content">
                <p className="stat-label">Total de Servicios</p>
                <h3 className="stat-value">{stats.total}</h3>
                <p className="stat-subtitle">Servicios registrados</p>
              </div>
              <div className="stat-icon-container">
                <Activity className="stat-icon" />
              </div>
            </div>
          </div>

          <div className="col">
            <div 
              className={`stat-card stat-success ${activeTab === 'en_ruta' ? 'active-stat' : ''}`}
              onClick={() => setActiveTab('en_ruta')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-content">
                <p className="stat-label">En Ruta</p>
                <h3 className="stat-value">{stats.en_ruta}</h3>
                <p className="stat-subtitle">Servicios activos</p>
              </div>
              <div className="stat-icon-container">
                <Route className="stat-icon" />
              </div>
            </div>
          </div>

          <div className="col">
            <div 
              className={`stat-card stat-warning ${activeTab === 'problemas' ? 'active-stat' : ''}`}
              onClick={() => setActiveTab('problemas')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-content">
                <p className="stat-label">En Progreso</p>
                <h3 className="stat-value">{stats.en_progreso}</h3>
                <p className="stat-subtitle">En desarrollo</p>
              </div>
              <div className="stat-icon-container">
                <Clock className="stat-icon" />
              </div>
            </div>
          </div>

          <div className="col">
            <div 
              className={`stat-card stat-complete ${activeTab === 'completados' ? 'active-stat' : ''}`}
              onClick={() => setActiveTab('completados')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-content">
                <p className="stat-label">Completados</p>
                <h3 className="stat-value">{stats.completados}</h3>
                <p className="stat-subtitle">Servicios finalizados</p>
              </div>
              <div className="stat-icon-container">
                <Check className="stat-icon" />
              </div>
            </div>
          </div>

          <div className="col">
            <div 
              className={`stat-card stat-danger ${activeTab === 'cancelados' ? 'active-stat' : ''}`}
              onClick={() => setActiveTab('cancelados')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-content">
                <p className="stat-label">Cancelados</p>
                <h3 className="stat-value">{stats.cancelados}</h3>
                <p className="stat-subtitle">Servicios cancelados</p>
              </div>
              <div className="stat-icon-container">
                <X className="stat-icon" />
              </div>
            </div>
          </div>
        </div>

        {/* Services Table */}
        <div className="card custom-card mb-4">
          <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="table-title">Servicios de Transporte</h2>
                <div className="table-info">
                  <div className="d-flex align-items-center gap-3">
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por expediente, cliente, ruta..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          border: '1px solid #dee2e6',
                          minWidth: '250px',
                          paddingLeft: '2.5rem',
                          fontSize: '0.875rem'
                        }}
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="#6c757d"
                        className="bi bi-search position-absolute"
                        viewBox="0 0 16 16"
                        style={{
                          left: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                      </svg>
                    </div>
                    <span className="table-count">
                      Mostrando {Math.min(indexOfFirstItem + 1, filteredServices.length)}-{Math.min(indexOfLastItem, filteredServices.length)} de {filteredServices.length} servicios
                    </span>
                    <span className="table-badge">Última actualización: {lastUpdated.toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>
          </div>

          <div className="table-responsive">
            <table className="table custom-table">
              <thead>
                <tr>
                  <th 
                    onClick={() => requestSort('expediente')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      ID/Vehículo
                      {sortConfig.key === 'expediente' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  {/* agregar fecha y hora_inicial  */}
                  <th>Fecha y Hora Inicial</th>
                  <th 
                    onClick={() => requestSort('cliente')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      Usuario
                      {sortConfig.key === 'cliente' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('ruta')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      Ruta
                      {sortConfig.key === 'ruta' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('estado')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      Estado
                      {sortConfig.key === 'estado' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    onClick={() => requestSort('conductor')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      Conductor
                      {sortConfig.key === 'conductor' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Contacto</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((service) => {
                  const statusDisplay = getStatusDisplay(service.estado_servicio)
                  const VehicleIcon = getVehicleIcon(service.vehiculo)

                  return (
                    <tr key={service.id} onClick={() => setSelectedService(service)}>
                      <td>
                        <div className="service-info">
                          <div className={`service-icon ${statusDisplay.bgClass}`}>
                            <VehicleIcon className={`icon-md ${statusDisplay.colorClass}`} />
                          </div>
                          <div>
                            <div className="service-id">{service.numero_expediente || service.id}</div>
                            <div className="service-vehicle">{service.vehiculo}</div>
                          </div>
                        </div>
                      </td>
                      {/* fecha y hora inicial */}
                      <td>{service.fecha} {service.hora_inicial}</td>
                      <td>
                        <div>
                          <div className="route-destination">{service.nombre_cliente}</div>
                          <div className="route-origin">ID: {service.identificacion}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="route-destination">{service.direccion_final}</div>
                          <div className="route-origin">desde {service.direccion_inicial}</div>
                        </div>
                      </td>
                      <td>
                        <div className="service-status">
                          <div className={`status-indicator ${statusDisplay.indicatorClass}`}></div>
                          <span className="status-text">{statusDisplay.text}</span>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="route-destination">
                            {service.conductor_nombre || `Conductor ${service.conductor}`}
                          </div>
                          <div className="route-origin">Placa: {service.vehiculo}</div>
                        </div>
                      </td>
                      <td className="update-time">{service.numero_contacto}</td>
                      <td>
                        <button
                          className="btn-details"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedService(service)
                            setShowModal(true)
                          }}
                        >
                          Detalles
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div>
                  <span className="text-muted small">
                    Página {currentPage} de {totalPages}
                  </span>
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </button>
                    </li>
                    
                    {getPageNumbers().map((number, index) => (
                      <li 
                        key={index} 
                        className={`page-item ${number === currentPage ? 'active' : ''} ${number === '...' ? 'disabled' : ''}`}
                      >
                        {number === '...' ? (
                          <span className="page-link">...</span>
                        ) : (
                          <button 
                            className="page-link" 
                            onClick={() => paginate(number)}
                          >
                            {number}
                          </button>
                        )}
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>

        {/* Service Detail Modal - COMPLETAMENTE REDISEÑADO */}
        {showModal && selectedService && (
          <div className="modal-overlay">
            <div className="custom-modal">
              <div className="modal-header">
                <div className="modal-title-container">
                  <div className={`modal-icon ${getStatusDisplay(selectedService.estado_servicio).bgClass}`}>
                    {React.createElement(getVehicleIcon(selectedService.vehiculo), {
                      className: `icon-lg ${getStatusDisplay(selectedService.estado_servicio).colorClass}`,
                    })}
                  </div>
                  <div>
                    <h3 className="modal-title">Servicio {selectedService.numero_expediente}</h3>
                    <div className="modal-status">
                      {React.createElement(getStatusDisplay(selectedService.estado_servicio).icon, {
                        className: "icon-sm",
                      })}
                      <span className="modal-status-text">
                        {getStatusDisplay(selectedService.estado_servicio).text}
                      </span>
                    </div>
                  </div>
                </div>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  <X className="icon-lg" />
                </button>
              </div>

              <div className="modal-body">
                <div className="row g-4">
                  <div className="col-lg-6">
                    <div className="info-card bg-primary-light">
                      <h4 className="info-card-title">
                        <User className="icon-md" />
                        Información del Usuario
                      </h4>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">
                            <User className="icon-sm" />
                            Usuario:
                          </span>
                          <span className="info-value">{selectedService.nombre_cliente}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <Car className="icon-sm" />
                            Vehículo:
                          </span>
                          <span className="info-value">{selectedService.vehiculo}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <User className="icon-sm" />
                            Conductor:
                          </span>
                          <span className="info-value">
                            {selectedService.conductor_nombre || `Conductor ${selectedService.conductor}`}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <Phone className="icon-sm" />
                            Contacto de Usuario:
                          </span>
                          <span className="info-value">{selectedService.numero_contacto}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <Calendar className="icon-sm" />
                            Fecha:
                          </span>
                          <span className="info-value">{selectedService.fecha}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <Clock className="icon-sm" />
                            Hora inicial:
                          </span>
                          <span className="info-value">{selectedService.hora_inicial}</span>
                        </div>
                      </div>

                      <div className="route-details">
                        <div className="route-item">
                          <span className="route-label">Punto de Origen</span>
                          <div className="route-value-container">
                            <MapPin className="route-icon" />
                            <span className="route-value">{selectedService.direccion_inicial}</span>
                          </div>
                        </div>
                        <div className="route-item">
                          <span className="route-label">Destino Final</span>
                          <div className="route-value-container">
                            <Locate className="route-icon" />
                            <span className="route-value">{selectedService.direccion_final}</span>
                          </div>
                        </div>
                        {/* Coordenadas GPS se muestran directamente en el mapa */}
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="info-card bg-success-light">
                      <h4 className="info-card-title">
                        <MapIcon className="icon-md" />
                        Ubicación y Ruta del Servicio
                      </h4>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">
                            <Activity className="icon-sm" />
                            Estado actual:
                          </span>
                          <span
                            className={`info-value ${getStatusDisplay(selectedService.estado_servicio).colorClass}`}
                          >
                            {getStatusInfo(selectedService.estado_servicio).name}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">
                            <FileText className="icon-sm" />
                            Expediente:
                          </span>
                          <span className="info-value">{selectedService.numero_expediente}</span>
                        </div>
                        {selectedService.observaciones && (
                          <div className="info-item">
                            <span className="info-label">
                              <FileText className="icon-sm" />
                              Observaciones:
                            </span>
                            <span className="info-value">{selectedService.observaciones}</span>
                          </div>
                        )}
                      </div>

                      {/* Mapa mejorado con ruta */}
                      {selectedService.latitud && selectedService.longitud && (
                        <div className="map-container">
                          <ServiceMap
                            latitude={selectedService.latitud}
                            longitude={selectedService.longitud}
                            serviceName={`${selectedService.numero_expediente} - ${selectedService.vehiculo}`}
                            originAddress={selectedService.direccion_inicial}
                            destinationAddress={selectedService.direccion_final}
                            onCoordsUpdate={(coords) => {
                              // Actualizar el servicio seleccionado con las nuevas coordenadas
                              setSelectedService((prev) => ({
                                ...prev,
                                latitud: coords.originLat.toString(),
                                longitud: coords.originLng.toString(),
                                // Añadir nuevos campos para las coordenadas de destino
                                destLatitud: coords.destLat.toString(),
                                destLongitud: coords.destLng.toString(),
                              }))
                            }}
                          />
                        </div>
                      )}

                      {/* Acciones del mapa mejoradas */}
                      <div className="map-actions">
                        {selectedService.latitud && selectedService.longitud && (
                          <button
                            className="btn-map"
                            onClick={() =>
                              openInMaps(
                                selectedService.latitud,
                                selectedService.longitud,
                                selectedService.numero_expediente,
                              )
                            }
                          >
                            <Navigation className="icon-sm" />
                            Abrir en Google Maps
                          </button>
                        )}

                        {selectedService.numero_contacto && (
                          <button className="btn-contact" onClick={() => callPhone(selectedService.conductor_movil)}>
                            <Phone className="icon-sm" />
                            Llamar al Conductor: {selectedService.conductor_movil}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="modal-footer-info">
                  <span className="footer-system">Sistema de Monitoreo - Rutas Mejoradas</span>
                  <span className="footer-update">Última actualización: {lastUpdated.toLocaleString()}</span>
                </div>
                <button className="btn-close-modal" onClick={() => setShowModal(false)}>
                  <X className="icon-sm" />
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
